import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { Clock, TrendingDown, FileText, AlertCircle, CheckCircle, XCircle } from "lucide-react";
import { formatWithTimezoneLabel, isDeadlinePassed } from "@/lib/timezone";

const SupplierHome = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    activeRfps: 0,
    successfulProposals: 0,
    unsuccessfulProposals: 0,
    pendingAuctions: 0,
  });
  const [recentActivity, setRecentActivity] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading && user) {
      fetchDashboardData();
    } else if (!authLoading && !user) {
      setLoading(false);
    }
  }, [user, authLoading]);

  const fetchDashboardData = async () => {
    if (!user?.email || !user?.id) {
      setLoading(false);
      return;
    }

    try {
      // Fetch active RFPs count - RFPs where user hasn't submitted a proposal
      // OR has submitted but auction hasn't happened yet
      const { data: allInvites } = await supabase
        .from("supplier_invitations")
        .select("rfp_id, rfps!inner(status, auction_start)")
        .eq("supplier_email", user.email);

      const { data: userProposals } = await supabase
        .from("proposals")
        .select("rfp_id, rfps!inner(status, auction_start)")
        .eq("user_id", user.id);

      const proposalRfpIds = new Set((userProposals || []).map((p: any) => p.rfp_id));
      
      // Active RFPs: RFPs where user hasn't submitted AND status is open for submissions
      // OR has submitted but auction hasn't started yet
      const now = new Date();
      const activeRfpsNoSubmission = (allInvites || []).filter((inv: any) => 
        inv.rfps && 
        (inv.rfps.status === "OPEN_FOR_SUBMISSIONS" || inv.rfps.status === "AWAITING_AUCTION") &&
        !proposalRfpIds.has(inv.rfp_id)
      ).length;

      // Count submitted proposals awaiting auction
      const submittedAwaitingAuction = (userProposals || []).filter((p: any) => 
        p.rfps && 
        (p.rfps.status === "OPEN_FOR_SUBMISSIONS" || p.rfps.status === "AWAITING_AUCTION") &&
        new Date(p.rfps.auction_start) > now
      ).length;

      // Fetch proposal counts by selection status
      const { count: successfulCount } = await supabase
        .from("proposals")
        .select("*", { count: "exact", head: true })
        .eq("user_id", user.id)
        .eq("selection_status", "successful");

      const { count: unsuccessfulCount } = await supabase
        .from("proposals")
        .select("*", { count: "exact", head: true })
        .eq("user_id", user.id)
        .eq("selection_status", "unsuccessful");

      // Fetch pending auctions - proposals where auction is pending or active
      const { data: pendingAuctions } = await supabase
        .from("proposals")
        .select("id, rfps!inner(status, auction_start)")
        .eq("user_id", user.id)
        .in("rfps.status", ["OPEN_FOR_SUBMISSIONS", "AWAITING_AUCTION", "AUCTION_ACTIVE"]);

      setStats({
        activeRfps: activeRfpsNoSubmission + submittedAwaitingAuction,
        successfulProposals: successfulCount || 0,
        unsuccessfulProposals: unsuccessfulCount || 0,
        pendingAuctions: (pendingAuctions || []).length,
      });

      // Fetch recent activity (last 5 proposals) with invitation token
      const { data: recentProposals } = await supabase
        .from("proposals")
        .select(`
          id,
          rfp_id,
          submitted_at,
          selection_status,
          invitation_id,
          rfps(title, matter_name),
          supplier_invitations!inner(invite_token)
        `)
        .eq("user_id", user.id)
        .order("submitted_at", { ascending: false })
        .limit(5);

      setRecentActivity(recentProposals || []);
    } catch (err) {
      console.error("Error fetching dashboard data:", err);
    } finally {
      setLoading(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-7xl mx-auto space-y-8">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Dashboard</h1>
          <p className="text-muted-foreground mt-2">Welcome back, {user?.user_metadata?.name || user?.email}</p>
        </div>

        {/* Stats Cards */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate("/supplier/active")}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Active RFPs</CardTitle>
              <FileText className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.activeRfps}</div>
              <p className="text-xs text-muted-foreground">Awaiting your proposal</p>
            </CardContent>
          </Card>

          <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate("/supplier/successful")}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Successful</CardTitle>
              <CheckCircle className="h-4 w-4 text-green-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">{stats.successfulProposals}</div>
              <p className="text-xs text-muted-foreground">Won proposals</p>
            </CardContent>
          </Card>

          <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate("/supplier/unsuccessful")}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Unsuccessful</CardTitle>
              <XCircle className="h-4 w-4 text-red-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-600">{stats.unsuccessfulProposals}</div>
              <p className="text-xs text-muted-foreground">Not selected</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Pending Auctions</CardTitle>
              <TrendingDown className="h-4 w-4 text-blue-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-600">{stats.pendingAuctions}</div>
              <p className="text-xs text-muted-foreground">Active or upcoming</p>
            </CardContent>
          </Card>
        </div>

        {/* Recent Activity */}
        <Card>
          <CardHeader>
            <CardTitle>Recent Activity</CardTitle>
          </CardHeader>
          <CardContent>
            {recentActivity.length === 0 ? (
              <div className="text-center text-muted-foreground py-8">
                <AlertCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No recent activity</p>
              </div>
            ) : (
              <div className="space-y-4">
                {recentActivity.map((activity) => (
                  <div 
                    key={activity.id} 
                    className="flex items-center justify-between p-4 border rounded-lg cursor-pointer hover:bg-muted/50 transition-colors"
                    onClick={() => {
                      const token = activity.supplier_invitations?.invite_token;
                      if (token) {
                        navigate(`/supplier/rfp/${activity.rfp_id}/view/${token}`);
                      }
                    }}
                  >
                    <div>
                      <p className="font-medium">{activity.rfps?.matter_name || activity.rfps?.title}</p>
                      <p className="text-sm text-muted-foreground">
                        Submitted: {formatWithTimezoneLabel(activity.submitted_at)}
                      </p>
                    </div>
                    <Badge 
                      variant={
                        activity.selection_status === "successful" ? "default" :
                        activity.selection_status === "unsuccessful" ? "destructive" : "secondary"
                      }
                      className={activity.selection_status === "successful" ? "bg-green-500" : ""}
                    >
                      {activity.selection_status === "successful" && <CheckCircle className="h-3 w-3 mr-1" />}
                      {activity.selection_status === "unsuccessful" && <XCircle className="h-3 w-3 mr-1" />}
                      {activity.selection_status.charAt(0).toUpperCase() + activity.selection_status.slice(1)}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SupplierHome;
